export const queryKeys = {
	// Auth
	auth: {
		all: ["auth"] as const,
		session: () => [...queryKeys.auth.all, "session"] as const,
		roles: () => [...queryKeys.auth.all, "roles"] as const,
	},

	// Pro
	pro: {
		all: ["pro"] as const,
		me: () => [...queryKeys.pro.all, "me"] as const,
		profile: (proId: string) =>
			[...queryKeys.pro.all, "profile", proId] as const,
		stats: (proId: string) => [...queryKeys.pro.all, "stats", proId] as const,
		whatsappRecipients: (proId: string) =>
			[...queryKeys.pro.all, "whatsappRecipients", proId] as const,
	},

	// Projects
	projects: {
		all: ["projects"] as const,
		lists: () => [...queryKeys.projects.all, "list"] as const,
		list: (proId: string) => [...queryKeys.projects.lists(), proId] as const,
		details: () => [...queryKeys.projects.all, "detail"] as const,
		detail: (proId: string, projectId: string) =>
			[...queryKeys.projects.details(), proId, projectId] as const,
	},

	// Photos
	photos: {
		all: ["photos"] as const,
		lists: () => [...queryKeys.photos.all, "list"] as const,
		list: (proId: string, projectId: string) =>
			[...queryKeys.photos.lists(), proId, projectId] as const,
	},

	// Taxonomy (rarely changes, long cache time)
	taxonomy: {
		all: ["taxonomy"] as const,
		businessTypes: () => [...queryKeys.taxonomy.all, "businessTypes"] as const,
		customerSegments: () =>
			[...queryKeys.taxonomy.all, "customerSegments"] as const,
		serviceCategories: () =>
			[...queryKeys.taxonomy.all, "serviceCategories"] as const,
		materialTags: () => [...queryKeys.taxonomy.all, "materialTags"] as const,
		brands: () => [...queryKeys.taxonomy.all, "brands"] as const,
		roomTypes: () => [...queryKeys.taxonomy.all, "roomTypes"] as const,
		cities: () => [...queryKeys.taxonomy.all, "cities"] as const,
		zones: (cityId: string) =>
			[...queryKeys.taxonomy.all, "zones", cityId] as const,
		localities: (zoneId: string) =>
			[...queryKeys.taxonomy.all, "localities", zoneId] as const,
		localitiesByCity: (cityId: string) =>
			[...queryKeys.taxonomy.all, "localitiesByCity", cityId] as const,
	},

	// Team
	team: {
		all: ["team"] as const,
		members: (proId: string) =>
			[...queryKeys.team.all, "members", proId] as const,
		invitations: (proId: string) =>
			[...queryKeys.team.all, "invitations", proId] as const,
	},

	// Website
	website: {
		all: ["website"] as const,
		settings: (proId: string) =>
			[...queryKeys.website.all, "settings", proId] as const,
		templates: (proId: string) =>
			[...queryKeys.website.all, "templates", proId] as const,
	},

	// Blogs
	blogs: {
		all: ["blogs"] as const,
		lists: () => [...queryKeys.blogs.all, "list"] as const,
		list: (proId: string, filters?: { status?: string }) =>
			[...queryKeys.blogs.lists(), proId, filters] as const,
		detail: (proId: string, blogId: string) =>
			[...queryKeys.blogs.all, "detail", proId, blogId] as const,
		tags: (proId: string) => [...queryKeys.blogs.all, "tags", proId] as const,
	},

	// Company Profile
	companyProfile: {
		all: ["companyProfile"] as const,
	},

	// Blog Images
	blogImages: {
		all: ["blogImages"] as const,
		lists: () => [...queryKeys.blogImages.all, "list"] as const,
		list: (proId: string, blogId: string) =>
			[...queryKeys.blogImages.lists(), proId, blogId] as const,
		pexelsSearch: (query: string, page: number) =>
			[...queryKeys.blogImages.all, "pexels", query, page] as const,
		proMedia: (proId: string, blogId: string, search: string, page: number) =>
			[
				...queryKeys.blogImages.all,
				"proMedia",
				proId,
				blogId,
				search,
				page,
			] as const,
		adminList: (blogId: string) =>
			[...queryKeys.blogImages.all, "adminList", blogId] as const,
		allProsMedia: (
			blogId: string,
			search: string,
			proId: string | undefined,
			page: number,
		) =>
			[
				...queryKeys.blogImages.all,
				"allProsMedia",
				blogId,
				search,
				proId,
				page,
			] as const,
		adminPexelsSearch: (query: string, page: number) =>
			[...queryKeys.blogImages.all, "adminPexels", query, page] as const,
	},

	// CRM
	crm: {
		all: ["crm"] as const,
		stages: (proId: string) => [...queryKeys.crm.all, "stages", proId] as const,
		sources: (proId: string) =>
			[...queryKeys.crm.all, "sources", proId] as const,
		kanban: (proId: string) => [...queryKeys.crm.all, "kanban", proId] as const,
		lead: (proId: string, leadId: string) =>
			[...queryKeys.crm.all, "lead", proId, leadId] as const,
		activities: (proId: string, leadId: string) =>
			[...queryKeys.crm.all, "activities", proId, leadId] as const,
		documents: (proId: string, leadId: string) =>
			[...queryKeys.crm.all, "documents", proId, leadId] as const,
		reminders: (proId: string, leadId: string) =>
			[...queryKeys.crm.all, "reminders", proId, leadId] as const,
		reminderCounts: (proId: string) =>
			[...queryKeys.crm.all, "reminderCounts", proId] as const,
		messages: (proId: string, leadId: string) =>
			[...queryKeys.crm.all, "messages", proId, leadId] as const,
		analytics: (proId: string, period?: string) =>
			[...queryKeys.crm.all, "analytics", proId, period] as const,
		settings: (proId: string) =>
			[...queryKeys.crm.all, "settings", proId] as const,
	},

	// Notifications
	notifications: {
		all: ["notifications"] as const,
		lists: () => [...queryKeys.notifications.all, "list"] as const,
		list: (filters?: {
			category?: string;
			search?: string;
			page?: number;
			limit?: number;
		}) => [...queryKeys.notifications.lists(), filters] as const,
		unreadCount: () => [...queryKeys.notifications.all, "unreadCount"] as const,
		subscriptions: () =>
			[...queryKeys.notifications.all, "subscriptions"] as const,
	},

	// Social Drafts (Social Studio)
	socialDrafts: {
		all: ["socialDrafts"] as const,
		lists: () => [...queryKeys.socialDrafts.all, "list"] as const,
		list: (proId: string) =>
			[...queryKeys.socialDrafts.lists(), proId] as const,
		detail: (proId: string, draftId: string) =>
			[...queryKeys.socialDrafts.all, "detail", proId, draftId] as const,
		download: (proId: string, draftId: string) =>
			[...queryKeys.socialDrafts.all, "download", proId, draftId] as const,
		musicTracks: (proId: string) =>
			[...queryKeys.socialDrafts.all, "musicTracks", proId] as const,
		templates: (proId: string) =>
			[...queryKeys.socialDrafts.all, "templates", proId] as const,
		voiceoverPreviewScript: (proId: string, projectId: string) =>
			[
				...queryKeys.socialDrafts.all,
				"voiceoverPreviewScript",
				proId,
				projectId,
			] as const,
	},

	// Settings
	settings: {
		all: ["settings"] as const,
		profile: () => [...queryKeys.settings.all, "profile"] as const,
		accounts: () => [...queryKeys.settings.all, "accounts"] as const,
	},

	// Onboarding
	onboarding: {
		all: ["onboarding"] as const,
		status: (proId: string) =>
			[...queryKeys.onboarding.all, "status", proId] as const,
	},

	// Admin
	admin: {
		all: ["admin"] as const,
		rrm: {
			all: ["admin", "rrm"] as const,
			funnel: () => [...queryKeys.admin.rrm.all, "funnel"] as const,
			prospects: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.rrm.all, "prospects", filters] as const,
			prospect: (id: string) =>
				[...queryKeys.admin.rrm.all, "prospect", id] as const,
			signups: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.rrm.all, "signups", filters] as const,
			inbox: () => [...queryKeys.admin.rrm.all, "inbox"] as const,
			ramp: () => [...queryKeys.admin.rrm.all, "ramp"] as const,
			tasks: (filters?: Record<string, string>) =>
				[...queryKeys.admin.rrm.all, "tasks", filters] as const,
			config: () => [...queryKeys.admin.rrm.all, "config"] as const,
			// Partner programme (SRS-PP-001). Separate keys from the recruitment
			// ones above: a referral is not a prospect, and invalidating one
			// must not silently refetch the other.
			referrals: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.rrm.all, "referrals", filters] as const,
			referral: (id: string) =>
				[...queryKeys.admin.rrm.all, "referral", id] as const,
			partners: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.rrm.all, "partners", filters] as const,
			partner: (id: string) =>
				[...queryKeys.admin.rrm.all, "partner", id] as const,
			payouts: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.rrm.all, "payouts", filters] as const,
			referralFunnel: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.rrm.all, "referral-funnel", filters] as const,
		},
		dashboard: () => [...queryKeys.admin.all, "dashboard"] as const,
		analytics: () => [...queryKeys.admin.all, "analytics"] as const,
		taxonomy: (type: string) =>
			[...queryKeys.admin.all, "taxonomy", type] as const,
		proLookup: () => [...queryKeys.admin.all, "proLookup"] as const,
		pros: {
			all: ["admin", "pros"] as const,
			list: (filters?: Record<string, string>) =>
				[...queryKeys.admin.pros.all, "list", filters] as const,
			detail: (proId: string) =>
				[...queryKeys.admin.pros.all, "detail", proId] as const,
			team: (proId: string) =>
				[...queryKeys.admin.pros.all, "team", proId] as const,
			stats: (proId: string) =>
				[...queryKeys.admin.pros.all, "stats", proId] as const,
			companyProfile: (proId: string) =>
				[...queryKeys.admin.pros.all, "companyProfile", proId] as const,
		},
		projects: {
			all: ["admin", "projects"] as const,
			list: (filters?: Record<string, string>) =>
				[...queryKeys.admin.projects.all, "list", filters] as const,
		},
		roomCategories: {
			all: ["admin", "roomCategories"] as const,
			list: () => [...queryKeys.admin.roomCategories.all, "list"] as const,
			detail: (code: string) =>
				[...queryKeys.admin.roomCategories.all, "detail", code] as const,
			media: (code: string, sortBy?: string) =>
				[...queryKeys.admin.roomCategories.all, "media", code, sortBy] as const,
		},
		homePage: {
			all: ["admin", "homePage"] as const,
			curation: () => [...queryKeys.admin.homePage.all, "curation"] as const,
		},
		calculatorConfig: {
			all: ["admin", "calculatorConfig"] as const,
			detail: () =>
				[...queryKeys.admin.calculatorConfig.all, "detail"] as const,
		},
		imports: {
			all: ["admin", "imports"] as const,
			list: (filters?: Record<string, string>) =>
				[...queryKeys.admin.imports.all, "list", filters] as const,
			detail: (importId: string) =>
				[...queryKeys.admin.imports.all, "detail", importId] as const,
			events: (importId: string, since?: number) =>
				[...queryKeys.admin.imports.all, "events", importId, since] as const,
		},
		// inquiries removed — CRM replaces pro/admin inquiry management
		users: {
			all: ["admin", "users"] as const,
			list: (filters?: Record<string, string>) =>
				[...queryKeys.admin.users.all, "list", filters] as const,
			detail: (userId: string) =>
				[...queryKeys.admin.users.all, "detail", userId] as const,
			sessions: (userId: string) =>
				[...queryKeys.admin.users.all, "sessions", userId] as const,
			accounts: (userId: string) =>
				[...queryKeys.admin.users.all, "accounts", userId] as const,
		},
		blogs: {
			all: ["admin", "blogs"] as const,
			list: (filters?: Record<string, string>) =>
				[...queryKeys.admin.blogs.all, "list", filters] as const,
			detail: (blogId: string) =>
				[...queryKeys.admin.blogs.all, "detail", blogId] as const,
			categories: () => [...queryKeys.admin.blogs.all, "categories"] as const,
			tags: () => [...queryKeys.admin.blogs.all, "tags"] as const,
			suggestions: (filters?: Record<string, string>) =>
				[...queryKeys.admin.blogs.all, "suggestions", filters] as const,
		},
		communications: {
			all: ["admin", "communications"] as const,
			list: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.communications.all, "list", filters] as const,
		},
		socialStudioMusic: () =>
			[...queryKeys.admin.all, "socialStudioMusic"] as const,
		socialStudioTemplates: () =>
			[...queryKeys.admin.all, "socialStudioTemplates"] as const,
		feedback: {
			all: ["admin", "feedback"] as const,
			list: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.feedback.all, "list", filters] as const,
		},
		consultations: {
			all: ["admin", "consultations"] as const,
			list: (filters?: Record<string, string | number>) =>
				[...queryKeys.admin.consultations.all, "list", filters] as const,
		},
		landingLeads: {
			all: ["admin", "landingLeads"] as const,
			list: (filters?: Record<string, string | number | boolean>) =>
				[...queryKeys.admin.landingLeads.all, "list", filters] as const,
			detail: (id: number) =>
				[...queryKeys.admin.landingLeads.all, "detail", id] as const,
		},
		whatsapp: {
			all: ["admin", "whatsapp"] as const,
			stats: () => [...queryKeys.admin.whatsapp.all, "stats"] as const,
			conversations: (filters?: Record<string, string>) =>
				[...queryKeys.admin.whatsapp.all, "conversations", filters] as const,
			conversation: (id: number) =>
				[...queryKeys.admin.whatsapp.all, "conversation", id] as const,
			templates: () => [...queryKeys.admin.whatsapp.all, "templates"] as const,
			campaigns: (filters?: Record<string, string>) =>
				[...queryKeys.admin.whatsapp.all, "campaigns", filters] as const,
			campaign: (id: number) =>
				[...queryKeys.admin.whatsapp.all, "campaign", id] as const,
			campaignRecipients: (id: number) =>
				[...queryKeys.admin.whatsapp.all, "campaignRecipients", id] as const,
			contacts: (filters?: Record<string, string>) =>
				[...queryKeys.admin.whatsapp.all, "contacts", filters] as const,
			checkPhone: (phone: string) =>
				[...queryKeys.admin.whatsapp.all, "checkPhone", phone] as const,
		},
	},
} as const;
